import { useSnackbar } from 'contexts/snackbar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import { Box, Button, Checkbox, Icon, IconButton, ResponsiveDialog, Stack, styled, Text } from 'shell/components';
import {
    convertCanvasRubric,
    convertD2LRubric,
    isUnscoredRubric,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';

const DIALOG_EXIT_ANIMATION_MS = 300;

const PushRubricDialog = ({ open, onClose, onSave, form, isQuizRubric = false }) => {
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [checked, setChecked] = useState(true);
    const [loading, setLoading] = useState(false);
    const [successResult, setSuccessResult] = useState(null);
    const canUseCompletionFallback =
        process.env.REACT_APP_KIND === 'CANVAS' && isUnscoredRubric(form) && usesCompleteIncompleteGrading(form);
    const requiresRubricSync = isUnscoredRubric(form) && !canUseCompletionFallback;
    const isSuccess = Boolean(successResult);
    const shouldSyncRubric = checked || requiresRubricSync;

    useEffect(() => {
        let resetTimeout;
        if (open) {
            setChecked(true);
        } else {
            resetTimeout = setTimeout(() => setSuccessResult(null), DIALOG_EXIT_ANIMATION_MS);
        }
        return () => clearTimeout(resetTimeout);
    }, [open]);

    const handleClose = () => {
        if (loading) return;
        onClose();
        if (successResult) onSave(successResult);
    };

    const handleClickSave = async () => {
        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');
            let pushedRubric = null;
            let newAPIData = null;

            if (shouldSyncRubric) {
                const pushRequest = await apiService.pushRubric({ accessToken, ...form });
                if (process.env.REACT_APP_KIND === 'CANVAS') {
                    newAPIData = await apiService.getAssignment({
                        course_id: form.api_data.course_id,
                        accessToken,
                        assignment_id: form.api_assignment_id,
                    });
                    const criteria = newAPIData?.rubric ?? pushRequest?.rubric?.criteria;
                    const settings = newAPIData?.rubric_settings ?? pushRequest?.rubric;
                    pushedRubric = criteria ? convertCanvasRubric(criteria, settings) : form.rubric;
                } else if (process.env.REACT_APP_KIND === 'D2L') {
                    const rubric =
                        pushRequest?.Assessment?.Rubrics?.[0] ??
                        (pushRequest?.rubric?.CriteriaGroups ? pushRequest.rubric : null) ??
                        (pushRequest?.CriteriaGroups ? pushRequest : null) ??
                        form.api_data?.Assessment?.Rubrics?.[0];
                    pushedRubric = rubric ? convertD2LRubric(rubric) : form.rubric;
                    newAPIData = form.api_data;
                }
            } else {
                pushedRubric = form.rubric;
                newAPIData = form.api_data;
            }
            const result = {
                api_data: newAPIData,
                rubric: pushedRubric,
            };
            if (shouldSyncRubric) {
                setSuccessResult(result);
            } else {
                onClose();
                onSave(result);
            }
        } catch {
            message.error(t('rubric.push-lms.sync-failed', 'Rubric sync failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <CustomResponsiveDialog
            $success={isSuccess}
            open={open}
            scroll={'paper'}
            onClose={handleClose}
            aria-labelledby={isSuccess ? 'rubric-sync-success-title' : 'rubric-sync-title'}
            aria-describedby={isSuccess ? 'rubric-sync-success-description' : 'rubric-sync-description'}
        >
            {isSuccess ? (
                <SuccessContent alignItems={'center'} justifyContent={'center'} spacing={5}>
                    <SuccessCloseButton
                        aria-label={t('common:controls.close', 'Close')}
                        kind={'exit'}
                        size={'medium'}
                        tooltip={{ component: t('common:controls.close', 'Close') }}
                        variant={'square'}
                        onClick={handleClose}
                    />
                    <SuccessIcon kind={'checkmark'} />
                    <SuccessDetails alignItems={'center'} spacing={7}>
                        <SuccessMessage alignItems={'center'} spacing={2}>
                            <SuccessTitle
                                id={'rubric-sync-success-title'}
                                component={'h2'}
                                variant={'sh1'}
                                weight={'semibold'}
                                align={'center'}
                            >
                                {isQuizRubric
                                    ? t('quiz:rubric.sync-success', 'Rubric Synced Successfully')
                                    : t('rubric.push-lms.sync-success', 'Rubric Synced Successfully')}
                            </SuccessTitle>
                            <SuccessDescription id={'rubric-sync-success-description'} align={'center'}>
                                {isQuizRubric
                                    ? t(
                                          'quiz:rubric.sync-success-description',
                                          'Confirmed rubric for this quiz is on your LMS. You can now close this window.'
                                      )
                                    : t(
                                          'rubric.push-lms.sync-success-description',
                                          'Confirmed rubric for this assignment is on your LMS. You can now close this window.'
                                      )}
                            </SuccessDescription>
                        </SuccessMessage>
                        <Button size={'medium'} onClick={handleClose}>
                            {t('common:controls.ok', 'Ok')}
                        </Button>
                    </SuccessDetails>
                </SuccessContent>
            ) : (
                <>
                    <Stack spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text id={'rubric-sync-title'} component={'h2'} variant={'h6'} weight={'medium'}>
                                {t('rubric.push-lms.sync-rubric', 'Sync Rubric To Your LMS?')}
                            </Text>
                            <IconButton
                                aria-label={t('common:controls.close', 'Close')}
                                disabled={loading}
                                kind={'exit'}
                                size={'medium'}
                                tooltip={{ component: t('common:controls.close', 'Close') }}
                                variant={'square'}
                                onClick={handleClose}
                            />
                        </Stack>
                        <Text id={'rubric-sync-description'} weight={'regular'}>
                            {isQuizRubric
                                ? t(
                                      'quiz:rubric.sync-description',
                                      'If you sync this rubric with your LMS, the TimelyGrader rubric will either replace your existing rubric or be added if no rubric currently exists.'
                                  )
                                : t(
                                      'rubric.push-lms.difference-note',
                                      'There are differences between the assignment rubric on your LMS and TimelyGrader. If you sync TimelyGrader’s rubric to your LMS, it will replace the existing rubric.'
                                  )}
                        </Text>
                    </Stack>
                    <CustomDialogContent>
                        <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
                            <Checkbox
                                aria-label={t('rubric.push-lms.sync-tg-rubric', 'Sync TimelyGrader rubric to LMS')}
                                checked={shouldSyncRubric}
                                disabled={requiresRubricSync || loading}
                                onChange={() => setChecked((current) => !current)}
                            />
                            <Stack sx={{ paddingTop: 3 }} spacing={2}>
                                <Text weight={'medium'}>
                                    {t('rubric.push-lms.sync-tg-rubric', 'Sync TimelyGrader rubric to LMS')}
                                </Text>
                                <CustomBox>
                                    <CustomText weight={'regular'}>
                                        <Text component={'span'} color={'inherit'} weight={'medium'}>
                                            {t('common:ui.note', 'NOTE').toUpperCase()}:{' '}
                                        </Text>
                                        {requiresRubricSync
                                            ? t(
                                                  'rubric.push-lms.unscored-difference-warning',
                                                  'This unscored rubric must be synced before rubric ratings can be passed back because there is no overall numeric grade fallback.'
                                              )
                                            : canUseCompletionFallback
                                              ? t(
                                                    'rubric.push-lms.completion-difference-warning',
                                                    "If you don't sync this rubric with your LMS, only each student's Complete, Incomplete, or Excused status will be passed back."
                                                )
                                              : t(
                                                    'rubric.push-lms.difference-warning',
                                                    'If you don’t sync this rubric with your LMS, only the overall final grade will be passed back for each student.'
                                                )}
                                    </CustomText>
                                </CustomBox>
                            </Stack>
                        </Stack>
                    </CustomDialogContent>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                        <Button variant={'tertiary'} color={'neutral'} disabled={loading} onClick={handleClose}>
                            {t('common:controls.cancel', 'Cancel')}
                        </Button>
                        <Button loading={loading} loadingGrow={loading} disabled={loading} onClick={handleClickSave}>
                            {isQuizRubric
                                ? shouldSyncRubric
                                    ? t('quiz:rubric.confirm-sync', 'Confirm & Sync')
                                    : t('common:controls.skip', 'Skip')
                                : shouldSyncRubric
                                  ? t('common:controls.confirm-and-proceed', 'Confirm & Proceed')
                                  : t('common:controls.skip-and-proceed', 'Skip & Proceed')}
                        </Button>
                    </Stack>
                </>
            )}
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog, {
    shouldForwardProp: (prop) => prop !== '$success',
})(({ $success, theme }) => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '600px',
            padding: theme.spacing($success ? 7 : 5),
            ...($success && {
                position: 'relative',
                maxWidth: 'calc(100% - 32px)',
                borderRadius: '20px',
                background: theme.palette.ensightful.neutral[50],
                boxShadow: theme.palette.ensightful.shadows[2],
            }),
        },
        ...($success && {
            '& .MuiDrawer-paper': {
                background: theme.palette.ensightful.neutral[50],
            },
            '& .MuiDrawer-paper > .MuiBox-root': {
                position: 'relative',
                padding: theme.spacing(7),
            },
        }),
    };
});

const SuccessContent = styled(Stack)(() => ({
    width: '100%',
    minHeight: '260px',
}));

const SuccessCloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(7),
    right: theme.spacing(7),
    zIndex: 1,
}));

const SuccessIcon = styled(Icon)(({ theme }) => ({
    width: theme.spacing(11),
    height: theme.spacing(11),
    boxSizing: 'border-box',
    padding: theme.spacing(2.5),
    flexShrink: 0,
    borderRadius: '50%',
    background: theme.palette.ensightful.success[600],
    color: theme.palette.ensightful.shades[0],
}));

const SuccessDetails = styled(Stack)(() => ({
    width: '100%',
}));

const SuccessMessage = styled(Stack)(() => ({
    width: '100%',
}));

const SuccessTitle = styled(Text)(({ theme }) => ({
    width: 'min(100%, 332px)',
    color: theme.palette.ensightful.neutral[800],
}));

const SuccessDescription = styled(Text)(({ theme }) => ({
    width: 'min(100%, 384px)',
    color: theme.palette.ensightful.neutral[800],
}));

const CustomDialogContent = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(4, 3, 6, 0),
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2, 3),
        background: theme.palette.ensightful.warning[100],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.warning[900],
    };
});

export default PushRubricDialog;
